import { useEffect, useMemo } from "react";

/**
 * Cache the filtered personalisation steps and slots.
 * @param {Object} options
 * @param {Map} options.personalisationSteps
 * @param {Map} options.personalisationSlots
 * @param {Object} options.selections
 */
export const useCacheFilteredPersonalisationStepsAndSlots = ({ personalisationSteps, personalisationSlots, selections }) => {

  const filteredPersonalisationSlots = useMemo(() => {
    return new Map(Array.from(personalisationSlots).filter(([slotId]) => !personalisationSlots.get(slotId).hidden));
  }, [personalisationSlots]);

  const filteredSelections = useMemo(() => {
    return {
      ...selections,
      slots: selections.slots.filter((slot) => !slot.hidden)
    };
  }, [selections]);

  const filteredPersonalisationSteps = useMemo(() => {
    return new Map(Array.from(personalisationSteps)
      .filter(([stepId]) => !personalisationSteps.get(stepId).hidden)
      .map(([stepId, step]) => [stepId, {
        ...step,
        slots: step.slots.filter((slotId) => {
          return !personalisationSlots.get(slotId)?.hidden
        })
      }]
      ));
  }, [personalisationSteps, personalisationSlots]);

  return {
    filteredPersonalisationSteps,
    filteredPersonalisationSlots,
    filteredSelections
  };
};

/**
 * Hide the steps.
 * @param {Object} options
 * @param {Map} options.personalisationSlots
 * @param {Function} options.setPersonalisationSteps
 * @param {Array} options.selectionsSlots
 */
export const useHideSteps = ({ personalisationSlots, setPersonalisationSteps, selectionsSlots }) => {
  // Remove any steps that don't have any slots left.
  useEffect(() => {
    setPersonalisationSteps((prev) => new Map(
      Array.from(prev).map(([stepId]) => {
        const filteredSlots = prev.get(stepId).slots
          .filter((slotId) => !personalisationSlots.get(slotId)?.hidden);
        return [stepId, {
          ...prev.get(stepId),
          hidden: filteredSlots.length === 0
        }]
      })
    ));
  }, [selectionsSlots, personalisationSlots, setPersonalisationSteps]);
}

/**
 * Hide the slots.
 * @param {Object} options
 * @param {Function} options.setPersonalisationSlots
 * @param {Array} options.hideSlotsOnVariantSelection
 */
export const hideSlots = ({ setPersonalisationSlots, hideSlotsOnVariantSelection }) => {
  // Mark the slots as hidden.
  setPersonalisationSlots((prev) => {
    return new Map(Array.from(prev).map(([slotId]) =>
      [slotId, {
        ...prev.get(slotId),
        hidden: hideSlotsOnVariantSelection.includes(slotId)
      }]
    ));
  })
}

/**
 * Hide the slot selections.
 * @param {Object} options
 * @param {Map} options.hideSlotsOnVariantsSettings
 * @param {string} options.productVariantId
 * @param {Function} options.setSelections
 * @param {Function} options.setPersonalisationSlots
 */
export const hideSlotSelections = ({ hideSlotsOnVariantsSettings, productVariantId, setSelections, setPersonalisationSlots }) => {
  // Check if this selection triggers removal of any slots.
  const hideSlotsOnVariantSelection = hideSlotsOnVariantsSettings.get(productVariantId);
  if (hideSlotsOnVariantSelection) {
    // Remove the slot from the selections.
    setSelections((prev) => ({
      ...prev,
      slots: prev.slots.map((slot) => ({
        ...slot,
        hidden: hideSlotsOnVariantSelection.includes(slot.id)
      }))
    }));
    // Mark the slots as hidden.
    hideSlots({ setPersonalisationSlots, hideSlotsOnVariantSelection });
  }
}

/**
 * Set all hidden to false.
 * @param {Object} options
 * @param {Function} options.setPersonalisationSteps
 * @param {Function} options.setPersonalisationSlots
 * @param {Function} options.setSelections
 */
export const setAllHiddenToFalse = ({ setPersonalisationSteps, setPersonalisationSlots, setSelections }) => {
  setPersonalisationSteps((prev) => new Map(Array.from(prev).map(([stepId]) => [stepId, {
    ...prev.get(stepId),
    hidden: false
  }]))
  );
  setPersonalisationSlots((prev) => new Map(Array.from(prev).map(([slotId]) => [slotId, {
    ...prev.get(slotId),
    hidden: false
  }]))
  );
  setSelections((prev) => ({
    ...prev,
    slots: prev.slots.map((slot) => ({
      ...slot,
      hidden: false
    }))
  }));
}